import { useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
    Flex,
    IconButton,
    Tooltip,
    HStack,
    Button,
    Text,
    AlertTitle,
} from '@chakra-ui/react';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import { useQuery } from 'hooks/useCustomQuery';
import { Error } from 'components/index';
import getAllServiceDefinitions from '@/API/ServiceProfiles/getAllServiceDefinitions';
import { BsThreeDotsVertical } from 'react-icons/bs';
import AddOperatorPlatformServices from './AddOperatorPlatformServices';
import { Alert, AlertIcon, AlertDescription } from '@/components/v4';
import { VStack } from 'rsuite';
import { getWholesalerServiceProfiles } from 'API/ServiceProfiles/getAllServiceProfiles';

const AddButton = ({ setMode }) => {
    return (
        <div style={{ paddingLeft: '20px' }}>
            <HStack spacing={2}>
                <Tooltip
                    label="Add Operator Platform Service"
                    placement="bottom"
                    hasArrow
                    portalProps={{ containerRef: undefined }}>
                    <div
                        style={{
                            position: 'relative',
                            display: 'inline-block',
                        }}>
                        {' '}
                        <Button
                            size="sm"
                            onClick={() => setMode('Create')}
                            // data-walkthroughid={ufAddApprovalCodeBtn}
                        >
                            + Add Service
                        </Button>
                    </div>
                </Tooltip>
            </HStack>
        </div>
    );
};

const OperatorServices = () => {
    const [selectedRowData, setSelectedRowData] = useState();
    const [mode, setMode] = useState('Get');
    const tableRef = useRef(null);
    const { currentWholesaler: wholesalerID, wholesalers } = useSelector(
        (state) => state.navigationLists,
    );
    const { currentScope } = useSelector((state) => {
        return { ...state.login };
    });

    // Fetch wholesaler service profiles
    const { data: wholesalerServicesData } = useQuery({
        queryKey: ['wholesalerServiceProfiles', wholesalerID],
        queryFn: () => getWholesalerServiceProfiles({ wholesalerID }),
        enabled: !!wholesalerID,
    });

    const currentWholesalerServices = useMemo(() => {
        return wholesalerServicesData?.data || [];
    }, [wholesalerServicesData]);

    const hasOperatorPlatform = useMemo(() => {
        return currentWholesalerServices?.some(
            (service) => service?.serviceType === 'OperatorPlatform',
        );
    }, [currentWholesalerServices]);

    const { darkMode } = useSelector((state) => state.settings);
    const wholesalerName = useMemo(() => {
        return wholesalers?.find(({ id }) => id === wholesalerID)?.name;
    }, [wholesalers, wholesalerID]);

    // Fetch service definitions
    const { refetch, isRefetching, isFetching, status, data } = useQuery({
        queryKey: ['serviceDefinitions', currentScope],
        queryFn: () =>
            getAllServiceDefinitions({ wholesalerID, scope: currentScope }),
        enabled: !!wholesalerID,
        retry: 1,
    });

    const editOperatorService = (rowData) => {
        setSelectedRowData(rowData);
        setMode('Edit');
    };
    // Table columns with edit button as last column
    const columns = useMemo(
        () => [
            {
                headerName: 'Vendor Name',
                field: 'name',
                cellRenderer: () => {
                    return wholesalerName || '';
                },
            },
            {
                headerName: 'Service Name',
                field: 'name',
            },
            {
                headerName: 'Service ID',
                field: 'id',
            },
            {
                headerName: 'Trunk Segment',
                field: 'trunkSegment',
            },
            {
                headerName: 'TRE Enabled',
                field: 'treEnabled',
                cellRenderer: (params) => {
                    return params.value ? '✓' : '−';
                },
            },
            {
                headerName: 'Action',
                field: 'action',
                // 70 was below the grid's default 100 minWidth, so the header
                // clamped down and rendered as "A...".
                maxWidth: 110,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }),
                lockPosition: 'right',
                cellRenderer: (params) => (
                    <HStack
                        spacing={0}
                        justifyContent="center"
                        alignItems="center"
                        width="100%"
                        height="100%">
                        <Tooltip label="Edit Approval Code">
                            <IconButton
                                icon={
                                    <BsThreeDotsVertical
                                        fill={darkMode ? 'white' : 'black'}
                                    />
                                }
                                aria-label="Edit"
                                variant="ghost"
                                onClick={() => editOperatorService(params.data)}
                                _hover={{
                                    bg: darkMode
                                        ? 'whiteAlpha.200'
                                        : 'blackAlpha.100',
                                }}
                                _active={{
                                    bg: darkMode
                                        ? 'whiteAlpha.300'
                                        : 'blackAlpha.200',
                                }}
                                _focus={{ boxShadow: 'none' }}
                            />
                        </Tooltip>
                    </HStack>
                ),
            },
        ],
        [darkMode, wholesalerName],
    );

    const tableDataObj = {
        columns: columns,
        data: data?.data,
    };

    if (!wholesalerID) {
        return <Error error="No partner selected." />;
    }

    return (
        <>
            {mode === 'Get' && (
                <>
                    {/* <StateToasts type={successAction} /> */}
                    <Text
                        sx={{
                            fontSize: '20',
                            fontWeight: '500',
                            marginBottom: '15px',
                        }}>
                        Operator Platform Services
                    </Text>
                    <AGGridMain
                        gridRef={tableRef}
                        tableData={tableDataObj}
                        masterDetail={false}
                        isLoading={status === 'loading'}
                        isFetching={isFetching}
                        isRefetching={isRefetching}
                        titleName="Operator Platform Services Codes"
                        refetch={refetch}
                        extraComponents={
                            <Flex gap="4">
                                <AddButton setMode={setMode} />
                            </Flex>
                        }
                    />
                </>
            )}

            {(mode === 'Edit' || mode === 'Create') && hasOperatorPlatform && (
                <AddOperatorPlatformServices
                    data={mode === 'Edit' ? selectedRowData : null}
                    mode={mode}
                    handleBackButton={() => setMode('Get')}
                />
            )}

            {(mode === 'Edit' || mode === 'Create') && !hasOperatorPlatform && (
                <Alert status="error">
                    <VStack>
                        <HStack>
                            <AlertIcon />
                            <AlertTitle>
                                Cannot Create Operator Platform Service
                            </AlertTitle>
                        </HStack>
                        <AlertDescription marginLeft="40px">
                            The current Wholesaler does not have access to an
                            Operator Platform service profile.
                        </AlertDescription>
                    </VStack>
                </Alert>
            )}
        </>
    );
};

export default OperatorServices;
